import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { KeyRound, Loader2, BadgeCheck } from "lucide-react";

const EMPTY = { file_code: "", current_password: "", new_password: "", confirm: "" };
const ROLE = { admin: "Administrador", user: "Personal", driver: "Conductor" };

export default function ProfilePage() {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) return toast.error("La nueva contraseña debe tener al menos 8 caracteres");
    if (form.new_password !== form.confirm) return toast.error("Las contraseñas nuevas no coinciden");
    setBusy(true);
    try {
      const { confirm, ...body } = form;
      await api.post("/auth/change-password", body);
      toast.success("Contraseña cambiada correctamente");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const field = (k, label, props = {}) => (
    <div>
      <Label className="text-[10px] uppercase tracking-widest">{label}</Label>
      <Input required value={form[k]} onChange={set(k)} className="h-11 rounded-none border-black mt-1" data-testid={`profile-${k.replace("_", "-")}`} {...props} />
    </div>
  );

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-3xl" data-testid="profile-page">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Cuenta</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 mb-6">Mi perfil</h1>

      <div className="border-2 border-black bg-white p-5 mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-display text-xl font-bold">{user?.name}</div>
          <div className="text-sm text-zinc-600">{user?.email} · {ROLE[user?.role] || user?.role}</div>
        </div>
        <div className="bg-black text-white px-4 py-3" data-testid="profile-file-code">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 flex items-center gap-1"><BadgeCheck size={12} /> Nº de expediente</div>
          <div className="font-mono text-2xl font-bold tracking-[0.3em]">{user?.file_code || "——"}</div>
        </div>
      </div>
      <p className="text-xs text-zinc-500 -mt-4 mb-6">Tu nº de expediente es personal e intransferible. No lo compartas con nadie.</p>

      <form onSubmit={submit} className="border-2 border-black bg-white p-5 space-y-4" data-testid="change-password-form">
        <div className="font-display font-bold flex items-center gap-2"><KeyRound size={18} /> Cambiar contraseña</div>
        {field("file_code", "Nº de expediente (6 dígitos)", { inputMode: "numeric", maxLength: 6, pattern: "\\d{6}" })}
        {field("current_password", "Contraseña actual", { type: "password", autoComplete: "current-password" })}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {field("new_password", "Nueva contraseña (mín. 8)", { type: "password", autoComplete: "new-password" })}
          {field("confirm", "Repite la nueva contraseña", { type: "password", autoComplete: "new-password" })}
        </div>
        <Button type="submit" disabled={busy} className="rounded-none h-11 bg-black hover:bg-[#E63946] w-full sm:w-auto" data-testid="change-password-submit">
          {busy && <Loader2 className="animate-spin mr-2" size={16} />} Guardar nueva contraseña
        </Button>
        <p className="text-xs text-zinc-500">¿Has olvidado tu contraseña? Escríbele a SofIA en el chat o usa «¿Olvidaste tu contraseña?» en el inicio de sesión y te pondrá con un supervisor.</p>
      </form>
    </div>
  );
}
